import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { txAPI, settingsAPI } from '../api';
import api from '../api';
import Money from '../components/Money';
import {
  IconArrowsExchange, IconChartBar, IconUsers, IconDatabase,
  IconBeach, IconBolt, IconArrowUp, IconArrowDown,
  IconArrowRight, IconScale, IconSettings, IconDoor, IconX
} from '@tabler/icons-react';

const MODULE_BUTTONS = [
  { label: 'İşlemler', sub: 'Gelir ve gider kayıtları', Icon: IconArrowsExchange, path: '/islemler' },
  { label: 'Raporlar', sub: 'Dönemsel analizler', Icon: IconChartBar, path: '/raporlar' },
  { label: 'Personel', sub: 'Maaş ve kayıtlar', Icon: IconUsers, path: '/personel' },
  { label: 'Yedekler', sub: 'Yedekleme ve arşiv', Icon: IconDatabase, path: '/yedekler' },
];

const AGENCY_BUTTONS = [
  { label: 'Turizm Acentesi', sub: 'Tur ve rezervasyon', Icon: IconBeach, path: '/acente' },
];

const ADMIN_BUTTONS = [
  { label: 'Ayarlar', sub: 'Firma ve kategoriler', Icon: IconSettings, path: '/ayarlar' },
  { label: 'Admin Paneli', sub: 'Kullanıcı yönetimi', Icon: IconBolt, path: '/admin' },
];

export default function Dashboard() {
  const { currentFirm, user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [form, setForm] = useState({ type: 'income', description: '', amount: '', currency: 'TRY', category: '', room: '', tx_date: new Date().toISOString().slice(0, 10), receipt: null });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(null);
  const [recentInc, setRecentInc] = useState([]);
  const [recentExp, setRecentExp] = useState([]);
  const fileRef = useRef();

  const load = useCallback(async () => {
    if (!currentFirm) return;
    const [sumRes, incRes, expRes, curRes, catRes] = await Promise.all([
      txAPI.getSummary(currentFirm.id, { period: 'yearly', year: new Date().getFullYear() }),
      txAPI.getAll(currentFirm.id, { type: 'income', limit: 5 }),
      txAPI.getAll(currentFirm.id, { type: 'expense', limit: 5 }),
      settingsAPI.getCurrencies(currentFirm.id),
      settingsAPI.getCategories(currentFirm.id),
    ]);
    setSummary(sumRes.data);
    setRecentInc(incRes.data.transactions);
    setRecentExp(expRes.data.transactions);
    setCurrencies(curRes.data);
    setCategories(catRes.data);
    const firstCat = catRes.data.filter(c => c.type === 'income')[0];
    if (firstCat) setForm(f => f.category ? f : ({ ...f, category: firstCat.name }));
  }, [currentFirm]);

  useEffect(() => {
    if (!currentFirm) return;
    api.get(`/firms/${currentFirm.id}/rooms`).then(r => setRooms(r.data || [])).catch(() => {});
  }, [currentFirm]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const openForm = (type) => {
    const cats = categories.filter(c => c.type === type);
    setForm({ type, description: '', amount: '', currency: 'TRY', category: cats[0]?.name || '', room: '', tx_date: new Date().toISOString().slice(0, 10), receipt: null });
    setShowForm(type);
    setError('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.description || !form.amount || !form.tx_date) { setError('Açıklama, tutar ve tarih alanlarını doldurun.'); return; }
    setSaving(true); setError('');
    try {
      await txAPI.create(currentFirm.id, form);
      setShowForm(null);
      if (fileRef.current) fileRef.current.value = '';
      load();
    } catch (err) { setError(err.response?.data?.error || 'Kayıt eklenemedi. Tekrar deneyin.'); }
    finally { setSaving(false); }
  };

  const totals = summary?.totals || [];
  const totalInc = parseFloat(totals.find(t => t.type === 'income')?.total || 0);
  const totalExp = parseFloat(totals.find(t => t.type === 'expense')?.total || 0);
  const net = totalInc - totalExp;

  if (!currentFirm) {
    return (
      <div className="empty">
        <strong>Henüz firma seçilmedi</strong>
        Soldaki firma seçicisinden çalışmak istediğiniz firmayı seçin.
      </div>
    );
  }

  const curCats = categories.filter(c => c.type === form.type);
  const isIncomeForm = showForm === 'income';
  const today = new Date().toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const ModuleBtn = ({ label, sub, Icon, path }) => (
    <button type="button" className="module-btn" onClick={() => navigate(path)}>
      <span className="module-icon"><Icon size={18} strokeWidth={1.6} /></span>
      <span>
        <span className="module-title" style={{ display: 'block' }}>{label}</span>
        <span className="module-sub" style={{ display: 'block' }}>{sub}</span>
      </span>
    </button>
  );

  const METRICS = [
    { label: 'Toplam gelir', value: totalInc, tone: 'pos', Icon: IconArrowUp },
    { label: 'Toplam gider', value: totalExp, tone: 'neg', Icon: IconArrowDown },
    { label: 'Net durum', value: net, tone: net >= 0 ? 'pos' : 'neg', Icon: IconScale },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      <div className="page-head">
        <h1>{currentFirm.name}</h1>
        <div className="sub">{today}</div>
      </div>

      {/* Yıllık özet — her rakamın altındaki çizgi, rakamın hesaplandığını söyler */}
      <div className="metric-grid">
        {METRICS.map(m => (
          <div className="metric" key={m.label}>
            <div className="metric-head">
              <span className="metric-label">{m.label}</span>
              <m.Icon size={16} strokeWidth={1.7} style={{ color: `var(--${m.tone})` }} />
            </div>
            <Money value={m.value} tone={m.tone} size="lg" />
            <span className="balance-rule" style={{ color: `var(--${m.tone})` }} />
            <div className="metric-note">Bu yıl</div>
          </div>
        ))}
      </div>

      {/* Hızlı eylemler */}
      <div className="action-bar">
        <button type="button" onClick={() => openForm('income')} className="btn btn-green">
          <IconArrowUp size={15} strokeWidth={2} /> Gelir ekle
        </button>
        <button type="button" onClick={() => openForm('expense')} className="btn btn-red">
          <IconArrowDown size={15} strokeWidth={2} /> Gider ekle
        </button>
        <button type="button" onClick={() => navigate('/islemler')} className="btn btn-ghost" style={{ marginLeft: 'auto' }}>
          Tüm işlemler <IconArrowRight size={14} strokeWidth={1.7} />
        </button>
        <button type="button" onClick={() => navigate('/raporlar')} className="btn btn-ghost">
          Raporlar <IconArrowRight size={14} strokeWidth={1.7} />
        </button>
      </div>

      {/* Kayıt formu */}
      {showForm && (
        <div className={`entry-panel${isIncomeForm ? '' : ' entry-panel-neg'}`}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{
              fontSize: 13.5, fontWeight: 650, display: 'flex', alignItems: 'center', gap: 7,
              color: isIncomeForm ? 'var(--pos)' : 'var(--neg)',
            }}>
              {isIncomeForm ? <IconArrowUp size={16} strokeWidth={2} /> : <IconArrowDown size={16} strokeWidth={2} />}
              {isIncomeForm ? 'Gelir ekle' : 'Gider ekle'}
            </div>
            <button type="button" onClick={() => setShowForm(null)} className="btn btn-ghost btn-sm btn-icon" aria-label="Formu kapat">
              <IconX size={14} strokeWidth={1.8} />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="entry-grid">
              <div style={{ gridColumn: 'span 2' }}>
                <label className="field-label" htmlFor="d-desc">Açıklama</label>
                <input id="d-desc" className="field-input" value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Örn. Mayıs ayı konaklama tahsilatı" />
              </div>
              <div>
                <label className="field-label" htmlFor="d-amount">Tutar</label>
                <input id="d-amount" className="field-input" type="number" min="0" step="0.01"
                  value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                  placeholder="0,00" />
              </div>
              <div>
                <label className="field-label" htmlFor="d-cur">Döviz</label>
                <select id="d-cur" className="field-select" value={form.currency}
                  onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}>
                  {currencies.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
                </select>
              </div>
              <div>
                <label className="field-label" htmlFor="d-cat">Kategori</label>
                <select id="d-cat" className="field-select" value={form.category}
                  onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                  {curCats.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="field-label" htmlFor="d-date">Tarih</label>
                <input id="d-date" className="field-input" type="date" value={form.tx_date}
                  onChange={e => setForm(f => ({ ...f, tx_date: e.target.value }))} />
              </div>
              {rooms.length > 0 && isIncomeForm && (
                <div>
                  <label className="field-label" htmlFor="d-room">Oda</label>
                  <select id="d-room" className="field-select" value={form.room || ''}
                    onChange={e => setForm(f => ({ ...f, room: e.target.value }))}>
                    <option value="">Seçilmedi</option>
                    {rooms.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
                  </select>
                </div>
              )}
              <div style={{ gridColumn: 'span 2' }}>
                <label className="field-label" htmlFor="d-file">
                  Fiş veya dekont <span style={{ color: 'var(--fg-3)', fontWeight: 400 }}>— isteğe bağlı</span>
                </label>
                <input id="d-file" ref={fileRef} type="file" accept="image/*,.pdf"
                  onChange={e => setForm(f => ({ ...f, receipt: e.target.files[0] || null }))}
                  className="field-input" style={{ paddingTop: 6, fontSize: 12 }} />
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16 }}>
              <button type="submit" disabled={saving} className={`btn ${isIncomeForm ? 'btn-green' : 'btn-red'}`}>
                {saving ? 'Kaydediliyor…' : isIncomeForm ? 'Geliri kaydet' : 'Gideri kaydet'}
              </button>
              <button type="button" onClick={() => setShowForm(null)} className="btn btn-ghost">Vazgeç</button>
            </div>

            {error && <div className="alert alert-error" style={{ marginTop: 12 }} role="alert">{error}</div>}
          </form>
        </div>
      )}

      {/* Son hareketler */}
      <div className="split-grid">
        {[
          { title: 'Son gelirler', tone: 'pos', items: recentInc, isInc: true },
          { title: 'Son giderler', tone: 'neg', items: recentExp, isInc: false },
        ].map(panel => (
          <div className="card" key={panel.title} style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{
              padding: '13px 18px', borderBottom: '1px solid var(--line-1)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div style={{
                fontSize: 13, fontWeight: 650, display: 'flex', alignItems: 'center', gap: 7,
                color: `var(--${panel.tone})`,
              }}>
                {panel.isInc ? <IconArrowUp size={15} strokeWidth={2} /> : <IconArrowDown size={15} strokeWidth={2} />}
                {panel.title}
              </div>
              <span className="count-pill">{panel.items.length} kayıt</span>
            </div>

            <div style={{ padding: '0 18px' }}>
              {panel.items.length === 0 ? (
                <div className="empty" style={{ padding: '30px 10px' }}>
                  <strong>Kayıt yok</strong>
                  {panel.isInc ? 'İlk gelirinizi ekleyin.' : 'İlk giderinizi ekleyin.'}
                </div>
              ) : panel.items.map(t => (
                <div className="tx-row" key={t.id}>
                  <span className={`tx-dir ${panel.isInc ? 'tx-dir-pos' : 'tx-dir-neg'}`}>
                    {panel.isInc ? <IconArrowUp size={14} strokeWidth={2.2} /> : <IconArrowDown size={14} strokeWidth={2.2} />}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 550, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.description}
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4, flexWrap: 'wrap' }}>
                      <span className={`badge ${panel.isInc ? 'badge-green' : 'badge-red'}`}>{t.category}</span>
                      {t.room && (
                        <span className="badge badge-gray"><IconDoor size={11} strokeWidth={1.8} />{t.room}</span>
                      )}
                      <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>{t.tx_date?.slice(0, 10)}</span>
                    </div>
                  </div>
                  <Money value={t.amount_try} tone={panel.tone} size="sm" sign />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Modüller */}
      <div>
        <div className="section-label">Muhasebe ve yönetim</div>
        <div className="module-grid">
          {MODULE_BUTTONS.map(b => <ModuleBtn key={b.path} {...b} />)}
        </div>

        {(currentFirm?.firm_type === 'agency' || user?.role === 'admin') && (
          <>
            <div className="section-label" style={{ marginTop: 22 }}>Ek modüller</div>
            <div className="module-grid">
              {currentFirm?.firm_type === 'agency' && AGENCY_BUTTONS.map(b => <ModuleBtn key={b.path} {...b} />)}
              {user?.role === 'admin' && ADMIN_BUTTONS.map(b => <ModuleBtn key={b.path} {...b} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
